import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ProjectService from '../../services/ProjectService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import ProjectTypeService from '../../services/ProjectTypeService';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const Project = () => {
    const [projects, setProjects] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterProjectType, setFilterProjectType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        startDate: '',
        endDate: '',
        status: 'Active',
        projectType: '',
        estimatedProjectCost: 0,
        estimatedReturns: 0,
        isActive: 1
    });
    const filteredProjects = useMemo(() => projects.filter((project) => {
        if (!searchText) return true;
        return filterList([project], searchText, ['name', 'projectTypeName', 'status', 'id']).length > 0;
    }), [projects, searchText]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterProjectType('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [currentPage, filterProjectType, filterStatus]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const projectTypes = await ProjectTypeService.getAll(1, 100);
            const projects = await ProjectService.getAll(currentPage, itemsPerPage, {
                projectType: filterProjectType,
                status: filterStatus
            });
            setProjectTypes(projectTypes.data || []);
            setProjects(projects.data || []);
            setPageCount(projects.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (project = null) => {
        if (project) {
            setEditMode(true);
            setFormData({
                id: project.id,
                name: project.name,
                startDate: project.startDate ? project.startDate.split('T')[0] : '',
                endDate: project.endDate ? project.endDate.split('T')[0] : '',
                status: project.status || 'Active',
                projectType: project.projectType || '',
                estimatedProjectCost: project.estimatedProjectCost || 0,
                estimatedReturns: project.estimatedReturns || 0,
                isActive: project.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                name: '',
                startDate: '',
                endDate: '',
                status: 'Active',
                projectType: '',
                estimatedProjectCost: 0,
                estimatedReturns: 0,
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: (name === 'isActive') ? parseInt(value) : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await ProjectService.update(formData.id, formData);
                utility.showSuccess('Project updated successfully');
            } else {
                await ProjectService.create(formData);
                utility.showSuccess('Project created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (project) => {
        try {
            const newStatus = project.isActive ? 0 : 1;
            await ProjectService.updateStatus(project.id, newStatus);
            utility.showSuccess(`Project status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        const selectedPage = event.selected + 1;
        setCurrentPage(selectedPage);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Project
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Projects</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            {/* Error alert removed in favor of professional toasts */}
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-md-4">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search projects..."
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label text-muted small">Project Type</label>
                                            <select className="form-select" value={filterProjectType} onChange={handleFilterChange(setFilterProjectType)}>
                                                <option value="">All Types</option>
                                                {projectTypes.map((type) => (
                                                    <option key={type.id} value={type.id}>{type.projectTypeName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                                <option value="Completed">Completed</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">&nbsp;</label>
                                            <button type="button" className="mt-1 btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Project Name</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Cost / Returns</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Dates</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredProjects.length === 0 ? (
                                                <tr><td colSpan="5" className="text-center py-4">No projects found.</td></tr>
                                            ) : (
                                                filteredProjects.map((project) => (
                                                    <tr key={project.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{project.name}</h6>
                                                                    <p className="text-xs text-secondary mb-0">Type: {project.projectTypeName || 'N/A'}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">Cost: ₹{project.estimatedProjectCost || 0}</p>
                                                            <p className="text-xs text-secondary mb-0">Ret: ₹{project.estimatedReturns || 0}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">Start: {project.startDate ? project.startDate.split('T')[0] : 'N/A'}</p>
                                                            <p className="text-xs text-secondary mb-0">End: {project.endDate ? project.endDate.split('T')[0] : 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${project.isActive ? 'success' : 'secondary'} cursor-pointer`}
                                                                onClick={() => handleToggleStatus(project)}
                                                            >
                                                                {project.isActive ? (project.status || 'Active') : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <Link to={`/cpnl/project-details/${project.id}`} className="btn btn-link text-primary mb-0">View Details</Link>
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(project)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="5">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Project' : 'Add New Project'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-12 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Project Name</label>
                                            <input type="text" name="name" className="form-control" value={formData.name} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Start Date</label>
                                            <input type="date" name="startDate" className="form-control" value={formData.startDate} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>End Date</label>
                                            <input type="date" name="endDate" className="form-control" value={formData.endDate} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Estimated Cost</label>
                                            <input type="number" name="estimatedProjectCost" className="form-control" value={formData.estimatedProjectCost} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Estimated Returns</label>
                                            <input type="number" name="estimatedReturns" className="form-control" value={formData.estimatedReturns} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Project Type</label>
                                            <select name="projectType" className="form-control" value={formData.projectType} onChange={handleInputChange}>
                                                <option value="">Select Type</option>
                                                {projectTypes.map((type) => (
                                                    <option key={type.id} value={type.id}>
                                                        {type.projectTypeName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Status</label>
                                            <select name="status" className="form-control" value={formData.status} onChange={handleInputChange}>
                                                <option value={"New"}>New</option>
                                                <option value={"Estimation"}>Estimation</option>
                                                <option value={"Cancelled"}>Cancelled</option>
                                                <option value={"Preproduction"}>Preproduction</option>
                                                <option value={"Production"}>Production</option>
                                                <option value={"PostProduction"}>Post Production</option>
                                                <option value={"Completed"}>Completed</option>
                                                <option value={"Released"}>Released</option>
                                                <option value={"Aborted"}>Aborted</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Is Active</label>
                                            <select name="isActive" className="form-control" value={formData.isActive} onChange={handleInputChange}>
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {editMode ? 'Updating...' : 'Saving...'}
                                            </>
                                        ) : (
                                            editMode ? 'Update' : 'Save'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Project;
